import type { PasswordPolicyName } from '@setplex/tria-api'
import type { Store } from 'effector'
import type { ContentWithPolicy, ParsedPolicy, ReadyPolicy } from './index.h'

import { api } from '@setplex/tria-api'
import { combine, createStore, restore } from 'effector'
import { persist } from 'effector-storage/local'
import { features } from '~/core/flags'
import { noop as noopEvent } from '~/shared/effector'
import { DEFAULT_POLICY, FROM, PASSWORD } from './index.h'

const parsePolicy = (content: ContentWithPolicy): ParsedPolicy | null => {
  if (!content?.passwordPolicy?.name || !content?.passwordPolicy?.pattern)
    return null

  const { name, pattern } = content.passwordPolicy

  const min = PASSWORD[name].MIN_LENGTH
  const max = PASSWORD[name].MAX_LENGTH
  const numbers = pattern.match(/\{(\d+),(\d+)\}\$$/)

  return {
    name: name as PasswordPolicyName,
    pattern,
    min: numbers ? parseInt(numbers[1], 10) || min : min,
    max: numbers ? parseInt(numbers[2], 10) || max : max,
  }
}

// this is separated from parsePolicy coz, RegExp cant be JSON
const convertPattern = (policy: ParsedPolicy | null): ReadyPolicy | null =>
  policy
    ? {
        name: policy.name,
        regex: new RegExp(policy.pattern),
        min: policy.min,
        max: policy.max,
      }
    : null

// /features
const $policyFeatures: Store<ParsedPolicy | null> =
  features.$featuredFlags.map(parsePolicy)

// /account
const $policyAccount: Store<ParsedPolicy | null> = restore(
  api.subscriber.getFx,
  null
).map(parsePolicy)

// /account/password/initiate
const $policyInitiate = createStore<ParsedPolicy | null>(null).on(
  api.subscriber.initResetPasswordFx.doneData,
  (_, payload) => parsePolicy(payload)
)

// store with policies by api key
export const $policy = combine(
  $policyFeatures,
  $policyAccount,
  $policyInitiate,
  (policyFeatures, policyAccount, policyInitiate) => {
    return {
      [FROM.FEATURES]: convertPattern(policyFeatures) ?? DEFAULT_POLICY,
      [FROM.ACCOUNT]: convertPattern(policyAccount) ?? DEFAULT_POLICY,
      [FROM.ACCOUNT_INITIATE]: convertPattern(policyInitiate) ?? DEFAULT_POLICY,
    }
  }
)

// In case user reload page while in reset password steps, initiate request not made, but saved result is needed

// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $policyInitiate,
  key: 'tria__policy-initiate',
  fail: noopEvent,
})
